import React, {useState, useEffect, useCallback} from "react"
import {useParams, useNavigate} from "react-router-dom"
import nprogress from "nprogress"
import swal from "sweetalert"
import useAuth from "auth/useAuth"
import useValues from "hooks/useValues"
import Notes from "components/rescuer/notes/Notes"
import ProfilePicture from "./ProfilePicture"
import RescuerTagsEdit from "./RescuerTagsEdit"
import RescuerEnabled from "./RescuerEnabled"
import {formatPhoneNumber, isValidJMBG} from "utils/utils"
import {isYearOnlyBirthdate} from "utils/date_utils"
import "styles/fields.css"
import "./RescuerEditPage.css"


const grades = {
    "": "Nema položeno skijanje",
    "A": "A",
    "A-": "A-",
    "B+": "B+"
}


/** https://javascript.plainenglish.io/how-to-pass-data-between-pages-in-react-router-dom-v6-e2f95afb7cdf */
export default function RescuerEditPage(props) {

    const {username} = useParams()
    const [rescuer, setRescuer] = useState({})
    const [permissions, setPermissions] = useState({})
    const [yearOnly, setYearOnly] = useState(false)
    const {auth, API} = useAuth()
    const navigate = useNavigate()
    const values = useValues("values")

    const load = useCallback(() => {
        auth(API.rescuersIdGet, {id: username}).then(response => {
            console.log(response)
            if (response.status === 200) {
                setRescuer(response.data.rescuer)
                setPermissions(response.data.permissions)
            }
        })
    }, [API.rescuersIdGet, auth, username])
    
    useEffect(() => {
        load()
    }, [load])
    
    useEffect(() => {
        setYearOnly(isYearOnlyBirthdate(rescuer.birthdate))
    }, [rescuer.birthdate])
    
    const onChange = (event) => {
        const {id, value} = event.target
        console.log(id, value)
        const resc = {...rescuer}
        resc[id] = value
        setRescuer(resc)
    }

    const toggleYearOnly = () => {
        const newYearOnly = !yearOnly
        setYearOnly(newYearOnly)
        if (newYearOnly && rescuer.birthdate) {
            // Switching to year-only: extract year from full date
            const year = rescuer.birthdate.substring(0, 4)
            setRescuer({...rescuer, birthdate: year})
        } else if (!newYearOnly) {
            // Switching to full date: clear value (can't construct full date from year)
            setRescuer({...rescuer, birthdate: ''})
        }
    }
        
    const save = () => {
        // Trim JMBG and phone number
        if (rescuer.jmbg) {
            rescuer.jmbg = rescuer.jmbg.trim()
        }

        if (rescuer.jmbg && !isValidJMBG(rescuer.jmbg)) {
            swal("Neispravan JMBG", "", 'error')
            return
        }

        rescuer.phone_number = formatPhoneNumber(rescuer.phone_number)

        console.log('Saving rescuer:', rescuer)
        nprogress.start()
        auth(API.rescuersIdPatch, {id: rescuer.username}, rescuer).then(response => {
            console.log('Save response:', response.data)
            nprogress.done()
            setRescuer(response.data.rescuer)
            setPermissions(response.data.permissions)
            navigateToProfile()
        }).catch(response => {
            console.error('Save error:', response)
            nprogress.done()
            swal("Greška", "Nije snimljeno", 'error')
        })
    }
    
    const navigateToProfile = () => {
        navigate(`/rescuer/${username}`)
    }
        
    
    
    return <div id="rescuer-editor" className="container">
    
    	<div className="row">
        	<div className="col-md-3 subsection">
                <ProfilePicture user={rescuer} reload={load}/>
                <RescuerTagsEdit username={username} permissions={permissions}/>                
            </div>
        
            <div className="col-md-4 fields">
                
                <div className="subsection">
                    <label htmlFor="name">Ime</label>
                    <input type="text" value={rescuer.given_name || ''} id="given_name" className="field" onChange={onChange}/>
            
                    <label htmlFor="family_name">Prezime</label>
                    <input type="text" value={rescuer.family_name || ''} id="family_name" className="field" onChange={onChange}/>
            
                    <label htmlFor="gss_id">GSS broj</label>
                    <input type="text" value={rescuer.gss_id || ''} id="gss_id" className="field" onChange={onChange}/>
                </div>

                <div className="subsection">
                    <label htmlFor="nickname">Nadimak</label>
                    <input type="text" value={rescuer.nickname || ''} id="nickname" className="field" onChange={onChange}/>
            
                    <label htmlFor="nickname2">Drugi nadimci (odvojeni zarezom)</label>
                    <input type="text" value={rescuer.nicknames || ''} id="nicknames" className="field" onChange={onChange}/>
                </div>

                <div className="subsection">
                    <label htmlFor="email">Email adresa</label>
                    <input type="text" value={rescuer.email || ''} id="email" className="field" onChange={onChange}/>                    
                    
                    <label htmlFor="phone_number">Broj telefona</label>
                    <input type="text" value={rescuer.phone_number || ''} id="phone_number" className="field" onChange={onChange}/>

                    <label htmlFor="address">Adresa</label>
                    <input type="text" value={rescuer.address || ''} id="address" className="field" onChange={onChange}/>
                    
                    <label htmlFor="city">Grad</label>
                    <input type="text" value={rescuer.city || ''} id="city" className="field" onChange={onChange}/>
                </div>
                
                <div className="subsection">
                    <label htmlFor="gender">Pol</label>
                    <select id="gender" value={rescuer.gender ?? ''} className="field" onChange={onChange}>
                        <option value="">Izaberite pol</option>
                        <option value="male">Muški</option>
                        <option value="female">Ženski</option>
                        <option value="other">Ostalo</option>
                    </select>

                    <label htmlFor="birthdate">Datum rođenja</label>
                    <div className="birthdate-input">
                        {yearOnly ? <input type="number" value={rescuer.birthdate || ''} id="birthdate" className="field" onChange={onChange} placeholder="GGGG" min="1900" max="2100"/>
                                  : <input type="date" value={rescuer.birthdate || ''} id="birthdate" className="field" onChange={onChange}/>}
                        <label className="year-only-toggle">
                            <input type="checkbox" checked={yearOnly} onChange={toggleYearOnly}/>
                            Samo godina
                        </label>
                    </div>
                    
                    <label htmlFor="jmbg">JMBG</label>
                    <input type="text" value={rescuer.jmbg || ''} id="jmbg" className="field" onChange={onChange}/>
                </div>
                
                <div className="subsection">
                    <label htmlFor="shirt_size">Veličina majice</label>
                    <select id="shirt_size" value={rescuer.shirt_size ?? ''} className="field" onChange={onChange}>
                        <option value="">Izaberite veličinu</option>
                        <option value="XS">XS</option>
                        <option value="S">S</option>
                        <option value="M">M</option>
                        <option value="L">L</option>
                        <option value="XL">XL</option>
                        <option value="XXL">XXL</option>
                        <option value="XXXL">XXXL</option>
                    </select>
                </div>
                
                <div className="subsection">
                    <label htmlFor="station">Stanica</label>
                    <select id="station" value={rescuer.station || ''} className="field" onChange={onChange}>
                        <option value="">Izaberite stanicu</option>
                        {values.get("stations")?.map(station => <option value={station} key={station}>{station}</option>)}
                    </select>
                                            
                    <label htmlFor="course_finished">Godina završetka kursa</label>
                    <input type="text" value={rescuer.admission_year || ''} id="admission_year" className="field" onChange={onChange}/>
                    
                    <label htmlFor="course_finished">Ocena skijanja</label>
                    <select id="ski" value={rescuer.ski || ''} className="field" onChange={onChange}>
                        {Object.keys(grades).map(grade => <option value={grade} key={grade}>{grades[grade]}</option>)}
                    </select>
                                        
                    <label htmlFor="pss_id">PSS broj (Planinarski savez Srbije)</label>
                    <input type="text" value={rescuer.pss_id ?? ''} id="pss_id" className="field" onChange={onChange}/>
                </div>
        
        		<div className="subsection" id="buttons">
                    <button className="btn btn-primary btn-lg" onClick={save}>Snimi</button>
        		</div>
        		
        		<RescuerEnabled username={username}/>
        	</div>
        	
        	<div className="col-md-5">
            	<Notes username={username} permissions={permissions}/>
        	</div>
        </div>
        		
    </div>

}